import { Plus, Trash } from "@phosphor-icons/react";

/**
 * Nominees editor — controlled list. Emits array of {id?, name, relation, phone, share_pct, address, dob}.
 * `value` and `onChange` come from parent state.
 */
export default function NomineesEditor({ value = [], onChange, compact = false }) {
  const rows = Array.isArray(value) ? value : [];

  const addRow = () =>
    onChange([...rows, { name: "", relation: "", phone: "", share_pct: "", address: "" }]);

  const removeRow = (idx) =>
    onChange(rows.filter((_, i) => i !== idx));

  const patchRow = (idx, patch) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-3" data-testid="nominees-editor">
      {rows.length === 0 && (
        <div className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg p-3 text-center">
          No nominee added.
        </div>
      )}

      {rows.map((n, idx) => (
        <div
          key={idx}
          className="border border-slate-200 rounded-lg p-3 space-y-2 relative bg-slate-50/40"
          data-testid={`nominee-row-${idx}`}
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Nominee {idx + 1}
            </div>
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="text-rose-500 hover:text-rose-700"
              data-testid={`nominee-remove-${idx}`}
              title="Remove nominee"
            >
              <Trash size={14} />
            </button>
          </div>
          <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"} gap-2`}>
            <TextField
              label="Name"
              required
              value={n.name || ""}
              onChange={(v) => patchRow(idx, { name: v })}
              testId={`nominee-name-${idx}`}
            />
            <TextField
              label="Relation"
              placeholder="Spouse / Son / …"
              value={n.relation || ""}
              onChange={(v) => patchRow(idx, { relation: v })}
              testId={`nominee-relation-${idx}`}
            />
            <TextField
              label="Phone"
              value={n.phone || ""}
              onChange={(v) => patchRow(idx, { phone: v })}
              testId={`nominee-phone-${idx}`}
            />
            <TextField
              label="Share %"
              type="number"
              step="0.01"
              value={n.share_pct ?? ""}
              onChange={(v) => patchRow(idx, { share_pct: v === "" ? null : Number(v) })}
              testId={`nominee-share-${idx}`}
            />
            <TextField
              label="Date of birth"
              type="date"
              value={n.dob || ""}
              onChange={(v) => patchRow(idx, { dob: v || null })}
              testId={`nominee-dob-${idx}`}
            />
            <TextField
              label="Address"
              value={n.address || ""}
              onChange={(v) => patchRow(idx, { address: v })}
              testId={`nominee-address-${idx}`}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-dashed border-emerald-300 rounded-lg px-3 py-1.5 hover:bg-emerald-50"
        data-testid="add-nominee-btn"
      >
        <Plus size={12} weight="bold" /> Add nominee
      </button>
    </div>
  );
}

function TextField({ label, value, onChange, required, type = "text", placeholder, testId, step }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-slate-500 mb-1">
        {label}{required && " *"}
      </div>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className="w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none bg-white"
      />
    </label>
  );
}
